import Sentry from "@similarweb/sentry-logger";

import type {
    CreateActivationParams,
    FirstPartyDataSource,
    ProjectsResponseSchema,
    ShortActivationSetSchema,
    UpdateActivationParams,
} from "app/pages/activation/types/api";
import type { IFetchService } from "app/services/fetchService";

import { ACTIVATION_USERDATA_API } from "../constants";

type ActivationApiServiceSchema = {
    getActivations(forceGetAllAvailable?: boolean): Promise<ProjectsResponseSchema | null>;
    getShortActivations(): Promise<ShortActivationSetSchema[]>;
    getActivationById(activationObjectId: string): Promise<ShortActivationSetSchema | null>;
    update(params: UpdateActivationParams): Promise<ShortActivationSetSchema>;
    delete(id: string, shouldRemoveKWList: boolean): Promise<void>;
    create(params: CreateActivationParams): Promise<ShortActivationSetSchema>;
    updateTrafficSource(activationId: string, trafficSource: FirstPartyDataSource): Promise<void>;
    switchFavorite(activationId?: string): Promise<void>;
};

export const activationApiService = (fetchService: IFetchService): ActivationApiServiceSchema => ({
    getActivations: async (forceGetAllAvailable = false): Promise<ProjectsResponseSchema | null> => {
        try {
            return await fetchService.get(ACTIVATION_USERDATA_API, { forceGetAllAvailable });
        } catch (error) {
            if (error.status === 404) {
                return null;
            }
            Sentry.errorTracker({
                message: "activationApiService: Error fetching activation object",
                e: error,
                level: "error",
            });
            throw error;
        }
    },
    getShortActivations: async (): Promise<ShortActivationSetSchema[]> => {
        try {
            return await fetchService.get(`${ACTIVATION_USERDATA_API}/list`);
        } catch (error) {
            if (error.status === 404) {
                return [];
            }
            Sentry.error(`Error fetching activations -`, error);
            throw error;
        }
    },
    getActivationById: async (activationObjectId: string): Promise<ShortActivationSetSchema | null> => {
        try {
            return await fetchService.get(`${ACTIVATION_USERDATA_API}/${activationObjectId}`);
        } catch (error) {
            if (error.status === 404) {
                return null;
            }
            Sentry.errorTracker({
                message: "activationApiService: Error fetching activation object",
                e: error,
                level: "error",
                params: {
                    extra: {
                        activationObjectId,
                    },
                },
            });
            throw error;
        }
    },

    update: async (params: UpdateActivationParams) => {
        try {
            return await fetchService.put(ACTIVATION_USERDATA_API, params);
        } catch (error) {
            Sentry.errorTracker({
                message: "activationApiService: Error updating activation object",
                e: error,
                level: "error",
                params: {
                    extra: {
                        ...(params || {}),
                    },
                },
            });
            throw new Error("Error while updating activation settings");
        }
    },
    delete: async (id: string, shouldRemoveKWList: boolean) => {
        try {
            return await fetchService.delete(`${ACTIVATION_USERDATA_API}/${id}`, {
                deleteRelatedKeywordGroups: shouldRemoveKWList,
            });
        } catch (error) {
            Sentry.errorTracker({
                message: "activationApiService: Error deleting activation object",
                e: error,
                level: "error",
                params: {
                    extra: {
                        activationId: id,
                    },
                },
            });
            throw new Error("Failed to delete activation object");
        }
    },
    create: async (params: CreateActivationParams) => {
        try {
            return await fetchService.post(ACTIVATION_USERDATA_API, params);
        } catch (error) {
            Sentry.errorTracker({
                message: "activationApiService: Error creating activation object",
                e: error,
                level: "error",
            });
            throw new Error("Failed to create activation object");
        }
    },
    updateTrafficSource: async (activationId: string, trafficSource: FirstPartyDataSource) => {
        try {
            return await fetchService.put(`${ACTIVATION_USERDATA_API}/${activationId}/trafficSource`, {
                value: trafficSource,
            });
        } catch (error) {
            Sentry.errorTracker({
                message: "activationApiService: Error updating activation traffic source",
                e: error,
                level: "error",
            });
            throw new Error("Failed to update activation traffic source");
        }
    },
    switchFavorite: async (activationId: string) => {
        try {
            return await fetchService.post(`${ACTIVATION_USERDATA_API}/${activationId}/switchFavorite`);
        } catch (error) {
            Sentry.error(`Error switching favorite activation`, error);
            throw new Error("Failed to switch favorite activation");
        }
    },
});
